<template>
  <div class="validacion-tareas-container p-4">
    <div class="mb-4 flex items-center justify-between">
      <vs-button icon="arrow_back" size="small" type="border" color="dark" @click="$router.push('/actividades-control')">
        Regresar al Control
      </vs-button>
    </div>

    <!-- Summary KPI Header -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <div class="stat-box bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center gap-3">
        <div class="p-3 bg-amber-50 text-amber-600 rounded-lg">
          <i class="feather icon-clock text-xl"></i>
        </div>
        <div>
          <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pendientes de Aprobación</span>
          <h4 class="text-xl font-bold text-amber-600">{{ contarPorEstado('pendiente') }}</h4>
        </div>
      </div>

      <div class="stat-box bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center gap-3">
        <div class="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
          <i class="feather icon-check-circle text-xl"></i>
        </div>
        <div>
          <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Validadas</span>
          <h4 class="text-xl font-bold text-emerald-600">{{ contarPorEstado('validada') }}</h4>
        </div>
      </div>

      <div class="stat-box bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center gap-3">
        <div class="p-3 bg-rose-50 text-rose-600 rounded-lg">
          <i class="feather icon-x-circle text-xl"></i>
        </div>
        <div>
          <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rechazadas</span>
          <h4 class="text-xl font-bold text-rose-600">{{ contarPorEstado('rechazada') }}</h4>
        </div>
      </div>
    </div>

    <vx-card title="Módulo de Validación y Aprobación de Tareas">
      <!-- Filtros -->
      <div class="flex flex-wrap items-end mb-6 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200/60">
        <div class="w-full sm:w-auto flex-1 min-w-[200px]">
          <label class="text-xs font-bold text-slate-600 uppercase mb-1 block">Departamento / Área</label>
          <v-select
            v-model="filtros.area"
            :options="areasDisponibles"
            label="tdp_nombre"
            placeholder="Todos los departamentos"
            class="w-full bg-white"
            @input="cargarTareasPendientes"
          />
        </div>
        <div class="w-full sm:w-auto flex-1 min-w-[220px]">
          <label class="text-xs font-bold text-slate-600 uppercase mb-1 block">Colaborador</label>
          <v-select
            v-model="filtros.empleado"
            :options="empleadosArea"
            :reduce="emp => emp.idusuario"
            label="nombre_completo"
            placeholder="Todos los colaboradores"
            class="w-full bg-white"
          />
        </div>
        <div class="w-full sm:w-auto flex-1 min-w-[180px]">
          <label class="text-xs font-bold text-slate-600 uppercase mb-1 block">Estado</label>
          <v-select
            v-model="filtros.estado"
            :options="estadosDisponibles"
            placeholder="Todos los estados"
            class="w-full bg-white"
          />
        </div>
      </div>

      <!-- Tabla de tareas pendientes -->
      <vs-table :data="tareasFiltradas" stripe search class="table-custom">
        <template slot="thead">
          <vs-th>Fecha</vs-th>
          <vs-th>Colaborador</vs-th>
          <vs-th>Categoría</vs-th>
          <vs-th>Título de Actividad</vs-th>
          <vs-th class="text-center">Cantidad</vs-th>
          <vs-th>Estado</vs-th>
          <vs-th class="text-center">Acciones</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr v-for="(tarea, index) in data" :key="index">
            <vs-td class="whitespace-nowrap font-medium text-slate-700">
              <i class="feather icon-calendar text-slate-400 mr-1"></i>
              {{ formatDate(tarea.fecha_tarea) }}
            </vs-td>
            <vs-td>
              <div v-if="tarea.usuario">
                <p class="font-bold text-slate-800 m-0">{{ tarea.usuario.nombres }} {{ tarea.usuario.apellidos }}</p>
                <span class="text-xs text-slate-500" v-if="tarea.departamento">
                  {{ tarea.departamento.tdp_nombre }}
                </span>
              </div>
            </vs-td>
            <vs-td>
              <vs-chip v-if="tarea.categoria" :color="tarea.categoria.color || 'primary'" class="font-semibold text-xs">
                {{ tarea.categoria.nombre_categoria }}
              </vs-chip>
            </vs-td>
            <vs-td class="font-medium text-slate-800">{{ tarea.titulo_tarea }}</vs-td>
            <vs-td class="text-center">
              <span class="inline-block bg-slate-100 text-slate-700 font-bold px-2.5 py-1 rounded-md text-xs">
                {{ tarea.cantidad }}
              </span>
            </vs-td>
            <vs-td>
              <vs-chip :color="getEstadoColor(tarea.estado)" class="font-bold text-xs uppercase tracking-wide">
                {{ getEstadoTexto(tarea.estado) }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <div class="flex justify-center gap-1">
                <vx-tooltip text="Ver detalle completo de la tarea">
                  <vs-button
                    size="small"
                    icon="visibility"
                    @click="verDetalleTarea(tarea)"
                    color="primary"
                    type="flat"
                  />
                </vx-tooltip>

                <vx-tooltip v-if="tarea.estado !== 'validada'" text="Aprobar y validar tarea">
                  <vs-button
                    size="small"
                    icon="check_circle"
                    @click="aprobarTarea(tarea)"
                    color="success"
                    type="flat"
                  />
                </vx-tooltip>

                <vx-tooltip v-if="tarea.estado !== 'rechazada'" text="Rechazar tarea con observaciones">
                  <vs-button
                    size="small"
                    icon="cancel"
                    @click="rechazarTarea(tarea)"
                    color="danger"
                    type="flat"
                  />
                </vx-tooltip>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <!-- Modal detalle tarea -->
    <vs-popup
      title="Detalle de Tarea"
      :active.sync="modalDetalle"
      fullscreen
    >
      <div v-if="tareaSeleccionada" class="p-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <strong>Empleado:</strong>
            {{ tareaSeleccionada.usuario.nombres }} {{ tareaSeleccionada.usuario.apellidos }}
          </div>
          <div>
            <strong>Fecha:</strong> {{ formatDate(tareaSeleccionada.fecha_tarea) }}
          </div>
          <div>
            <strong>Área:</strong> {{ tareaSeleccionada.categoria.area.nombre_area }}
          </div>
          <div>
            <strong>Categoría:</strong> {{ tareaSeleccionada.categoria.nombre_categoria }}
          </div>
          <div class="md:col-span-2">
            <strong>Título:</strong> {{ tareaSeleccionada.titulo_tarea }}
          </div>
          <div>
            <strong>Cantidad:</strong> {{ tareaSeleccionada.cantidad }}
          </div>
          <div>
            <strong>Estado:</strong>
            <vs-chip :color="getEstadoColor(tareaSeleccionada.estado)" class="ml-2">
              {{ getEstadoTexto(tareaSeleccionada.estado) }}
            </vs-chip>
          </div>
          <div class="md:col-span-2">
            <strong>Descripción:</strong>
            <p class="mt-2 p-3 bg-gray-100 rounded">{{ tareaSeleccionada.descripcion_tarea }}</p>
          </div>
          <div v-if="tareaSeleccionada.referencia_libro">
            <strong>Libro:</strong> {{ tareaSeleccionada.referencia_libro }}
          </div>
          <div v-if="tareaSeleccionada.referencia_pagina">
            <strong>Página:</strong> {{ tareaSeleccionada.referencia_pagina }}
          </div>
          <div v-if="tareaSeleccionada.observaciones" class="md:col-span-2">
            <strong>Observaciones:</strong>
            <p class="mt-2 text-danger">{{ tareaSeleccionada.observaciones }}</p>
          </div>
        </div>

        <!-- Validaciones previas -->
        <div v-if="tareaSeleccionada.validaciones && tareaSeleccionada.validaciones.length" class="mt-6">
          <h4 class="font-bold mb-3">Validaciones Registradas</h4>
          <div v-for="validacion in tareaSeleccionada.validaciones" :key="validacion.id_validacion" class="mb-3 p-3 border rounded">
            <div class="flex items-center gap-2">
              <vs-icon
                :icon="validacion.validado ? 'check_circle' : 'cancel'"
                :color="validacion.validado ? 'success' : 'danger'"
              />
              <span class="font-semibold">{{ validacion.validador.nombres }}</span>
              <span class="text-sm text-gray-500">({{ validacion.tipo_validador }})</span>
              <span class="text-xs text-gray-400 ml-auto">
                {{ formatDate(validacion.fecha_validacion) }}
              </span>
            </div>
            <p v-if="validacion.comentario" class="mt-2 text-sm">{{ validacion.comentario }}</p>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <vs-button @click="modalDetalle = false" color="dark" type="flat">
            Cerrar
          </vs-button>
          <vs-button
            v-if="tareaSeleccionada.estado !== 'validada'"
            @click="aprobarTarea(tareaSeleccionada)"
            color="success"
          >
            Aprobar
          </vs-button>
          <vs-button
            v-if="tareaSeleccionada.estado !== 'rechazada'"
            @click="rechazarTarea(tareaSeleccionada)"
            color="danger"
          >
            Rechazar
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Modal rechazo -->
    <vs-popup
      title="Rechazar Tarea"
      :active.sync="modalRechazo"
    >
      <div class="p-4">
        <p class="mb-4">Ingrese el motivo del rechazo:</p>
        <vs-textarea
          v-model="comentarioRechazo"
          class="w-full"
          rows="4"
          placeholder="Escriba aquí las razones del rechazo..."
        />
        <div class="flex justify-end gap-2 mt-4">
          <vs-button @click="modalRechazo = false" color="dark" type="flat">
            Cancelar
          </vs-button>
          <vs-button @click="confirmarRechazo" color="danger">
            Confirmar Rechazo
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { mapState } from 'vuex'

export default {
  name: 'ValidacionTareas',
  components: {
    vSelect
  },
  data() {
    return {
      tareas: [],
      areasDisponibles: [],
      empleadosArea: [],
      filtros: {
        area: null,
        empleado: null,
        estado: null
      },
      estadosDisponibles: [
        { label: 'Pendiente', value: 'pendiente' },
        { label: 'En Revisión', value: 'en_revision' },
        { label: 'Validada', value: 'validada' },
        { label: 'Rechazada', value: 'rechazada' }
      ],
      modalDetalle: false,
      modalRechazo: false,
      tareaSeleccionada: null,
      comentarioRechazo: '',
      esJefeArea: false,
      tipoValidador: 'jefe_area'
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    tareasFiltradas() {
      let resultado = this.tareas

      if (this.filtros.area) {
        resultado = resultado.filter(t => t.tdp_id === this.filtros.area.tdp_id)
      }

      if (this.filtros.empleado) {
        resultado = resultado.filter(t => t.idusuario === this.filtros.empleado)
      }

      if (this.filtros.estado) {
        resultado = resultado.filter(t => t.estado === this.filtros.estado.value)
      }

      return resultado
    },
    usuarioId() {
      return this.auth ? this.auth.idusuario : null
    }
  },
  mounted() {
    this.cargarAreas()
    this.cargarTareasPendientes()
  },
  methods: {
    async cargarAreas() {
      try {
        const response = await this.$http.get(this.$server_url + 'actividades-control/departamentos')
        this.areasDisponibles = response.data || []
      } catch (error) {
        console.error('Error al cargar áreas:', error)
      }
    },
    async cargarTareasPendientes() {
      this.$vs.loading()
      try {
        const response = await this.$http.get(this.$server_url + `actividades-control/validaciones/pendientes/${this.usuarioId}`)
        this.tareas = response.data || []
        
        // Extraer empleados únicos
        const empleadosMap = new Map()
        this.tareas.forEach(tarea => {
          if (tarea.idusuario && tarea.usuario) {
            if (!empleadosMap.has(tarea.idusuario)) {
              empleadosMap.set(tarea.idusuario, {
                idusuario: tarea.idusuario,
                nombre_completo: `${tarea.usuario.nombres} ${tarea.usuario.apellidos}`
              })
            }
          }
        })
        this.empleadosArea = Array.from(empleadosMap.values())
      } catch (error) {
        console.error('Error al cargar tareas:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudieron cargar las tareas',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    verDetalleTarea(tarea) {
      this.tareaSeleccionada = tarea
      this.modalDetalle = true
    },
    aprobarTarea(tarea) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: 'Aprobar Tarea',
        text: '¿Confirma que desea aprobar esta tarea?',
        accept: async () => {
          await this.validarTarea(tarea, true, 'Tarea aprobada')
        }
      })
    },
    rechazarTarea(tarea) {
      this.tareaSeleccionada = tarea
      this.comentarioRechazo = ''
      this.modalRechazo = true
    },
    async confirmarRechazo() {
      if (!this.comentarioRechazo) {
        this.$vs.notify({
          title: 'Atención',
          text: 'Debe ingresar un motivo para el rechazo',
          color: 'warning'
        })
        return
      }
      
      await this.validarTarea(this.tareaSeleccionada, false, this.comentarioRechazo)
      this.modalRechazo = false
    },
    async validarTarea(tarea, validado, comentario) {
      this.$vs.loading()
      try {
        await this.$http.post(this.$server_url + 'actividades-control/validaciones', {
          id_tarea: tarea.id_tarea,
          validador_id: this.usuarioId,
          tipo_validador: this.tipoValidador,
          validado: validado,
          comentario: comentario
        })

        this.$vs.notify({
          title: 'Éxito',
          text: validado ? 'Tarea aprobada correctamente' : 'Tarea rechazada correctamente',
          color: 'success'
        })

        this.modalDetalle = false
        this.cargarTareasPendientes()
      } catch (error) {
        console.error('Error al validar tarea:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudo completar la validación',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    getEstadoColor(estado) {
      const colores = {
        pendiente: 'warning',
        en_revision: 'primary',
        validada: 'success',
        rechazada: 'danger'
      }
      return colores[estado] || 'dark'
    },
    getEstadoTexto(estado) {
      const textos = {
        pendiente: 'Pendiente',
        en_revision: 'En Revisión',
        validada: 'Validada',
        rechazada: 'Rechazada'
      }
      return textos[estado] || estado
    },
    contarPorEstado(estado) {
      return this.tareasFiltradas.filter(t => t.estado === estado).length
    }
  }
}
</script>

<style scoped>
.validacion-tareas-container {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
